import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getMapPoints } from "../lib/api";

// How each location source is presented when a node is tapped.
const SOURCE_META = {
  gps: { color: "#39ff14", label: "Precise · opted in", icon: "◉" },
  ip: { color: "#ff9e2c", label: "Approximate · IP", icon: "≈" },
  timezone: { color: "#ff2bd6", label: "Region only · timezone", icon: "≈" },
};
function metaFor(src) {
  return SOURCE_META[src] || { color: "#00f0ff", label: "Approximate", icon: "≈" };
}

function makeIcon(point) {
  const m = metaFor(point.source);
  const ring = point.ongoing ? `<span class="fw-pulse" style="--c:${m.color}"></span>` : "";
  const beam = point.ongoing ? `<span class="fw-beam-up" style="--c:${m.color}"></span>` : "";
  const html = `<span class="fw-marker" style="--c:${m.color}">${beam}${ring}<span class="fw-dot"></span></span>`;
  return L.divIcon({
    html,
    className: "fw-divicon",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -12],
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Fit the viewport to the points once they load.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds.pad(0.35), { maxZoom: 9, animate: false });
  }, [points, map]);
  return null;
}

function PanelMapToggle({ navigate }) {
  return (
    <div className="fw-seg" role="tablist" aria-label="view">
      <span className="fw-seg-knob fw-knob-right" />
      <button type="button" role="tab" onClick={() => navigate("/")}>Panel</button>
      <button type="button" role="tab" className="active" aria-selected="true">Mapa</button>
    </div>
  );
}

export default function MapView() {
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [optIn, setOptIn] = useState(() => localStorage.getItem("fw_geo_optin") === "1");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const p = await getMapPoints(7);
      setPoints(Array.isArray(p) ? p : []);
      setErr(null);
    } catch (e) {
      setErr(e.message || "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOptIn = useCallback(() => {
    setOptIn((prev) => {
      const next = !prev;
      localStorage.setItem("fw_geo_optin", next ? "1" : "0");
      if (next && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(() => {}, () => {}, { maximumAge: 600000 });
      }
      return next;
    });
  }, []);

  const center = useMemo(() => {
    if (points.length) return [points[0].lat, points[0].lon];
    return [40.4168, -3.7038]; // default: Madrid-ish
  }, [points]);

  // Glowing "tracer" line linking activities in chronological order.
  const trail = useMemo(() => {
    return [...points]
      .filter((p) => p.lat != null && p.lon != null)
      .sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt))
      .slice(-40)
      .map((p) => [p.lat, p.lon]);
  }, [points]);

  const ongoingCount = points.filter((p) => p.ongoing).length;

  return (
    <div className="fw-crt-root">
      <style>{`
        .fw-crt-root{position:fixed;inset:0;display:flex;flex-direction:column;overflow:hidden;
          background:radial-gradient(120% 80% at 50% 8%, #1b0b3a 0%, #0c0820 55%, #070312 100%);
          color:#eae0cd;font-family:ui-monospace,SFMono-Regular,Menlo,monospace}

        /* header */
        .fw-crt-bar{display:flex;align-items:center;justify-content:space-between;gap:.5rem;
          padding:.6rem .8rem;z-index:40;border-bottom:1px solid #2a1750;
          background:linear-gradient(180deg,rgba(27,11,58,.85),rgba(12,8,32,.2))}
        .fw-crt-title{font-size:.95rem;letter-spacing:.34em;color:#eae0cd;
          text-shadow:-1.5px 0 #ff2bd6,1.5px 0 #00f0ff,0 0 12px #ff2bd699;animation:fwjit 3.2s steps(40) infinite}
        .fw-crt-sub{font-size:.58rem;letter-spacing:.2em;color:#9d7bd6;text-transform:uppercase;margin-top:2px}
        @keyframes fwjit{0%,97%,100%{transform:translateX(0)}98%{transform:translateX(-1px)}99%{transform:translateX(1px)}}

        /* segmented Panel/Mapa slider */
        .fw-seg{position:relative;display:inline-flex;align-items:center;border:1px solid #3a2470;
          background:#0c0820;border-radius:999px;padding:3px;box-shadow:0 0 12px #ff2bd633,inset 0 0 8px #00f0ff22}
        .fw-seg-knob{position:absolute;top:3px;bottom:3px;width:calc(50% - 3px);border-radius:999px;
          background:linear-gradient(90deg,#ff2bd6,#9d4bff);box-shadow:0 0 14px #ff2bd6aa;transition:left .22s ease}
        .fw-knob-right{left:calc(50%)}
        .fw-knob-left{left:3px}
        .fw-seg button{position:relative;z-index:1;padding:.4rem .9rem;border-radius:999px;background:transparent;
          border:none;font:inherit;font-size:.72rem;letter-spacing:.14em;text-transform:uppercase;color:#8a76bf;cursor:pointer}
        .fw-seg button.active{color:#fff;text-shadow:0 0 8px #fff8}

        .fw-btn{font:inherit;font-size:.68rem;letter-spacing:.12em;border-radius:999px;padding:.42rem .7rem;cursor:pointer;
          background:#0c0820;color:#9be7ff;border:1px solid #1f4a6b;text-transform:uppercase;white-space:nowrap}
        .fw-btn:hover{border-color:#00f0ff;color:#d6f8ff;box-shadow:0 0 12px #00f0ff55}
        .fw-btn-on{background:#1a0530;color:#ff8df0;border-color:#ff2bd6;box-shadow:0 0 14px #ff2bd677;text-shadow:0 0 8px #ff2bd6aa}

        .fw-map-wrap{position:relative;flex:1;min-height:0}
        .fw-map{position:absolute;inset:0;height:100%;width:100%;background:#070312}

        /* recolor the grayscale tiles into a neon violet/magenta duotone */
        .fw-crt-root .leaflet-tile-pane{filter:grayscale(1) sepia(1) saturate(5) hue-rotate(238deg) brightness(.62) contrast(1.15)}
        .fw-crt-root .leaflet-container{background:#070312}
        .fw-crt-root .leaflet-control-attribution{background:rgba(7,3,18,.7);color:#5b4a86;font-size:9px}
        .fw-crt-root .leaflet-control-attribution a{color:#9d7bd6}

        /* outrun laser floor grid — long converging neon lines */
        .fw-floor{position:absolute;left:-30%;right:-30%;bottom:0;height:46%;z-index:18;pointer-events:none;opacity:.55;
          transform:perspective(260px) rotateX(64deg);transform-origin:bottom center;
          background-image:
            repeating-linear-gradient(90deg, transparent 0 38px, rgba(0,240,255,.55) 38px 39px),
            repeating-linear-gradient(0deg, transparent 0 38px, rgba(255,43,214,.5) 38px 39px);
          -webkit-mask-image:linear-gradient(to top,#000 22%,transparent 100%);
          mask-image:linear-gradient(to top,#000 22%,transparent 100%);
          animation:fwfloor 5.5s linear infinite}
        @keyframes fwfloor{from{background-position:0 0,0 0}to{background-position:0 39px,0 39px}}
        /* neon horizon glow above the floor */
        .fw-horizon{position:absolute;left:0;right:0;bottom:46%;height:2px;z-index:19;pointer-events:none;opacity:.8;
          background:linear-gradient(90deg,transparent,#00f0ff 20%,#ff2bd6 80%,transparent);
          box-shadow:0 0 14px #ff2bd6cc,0 0 30px #00f0ff88}

        /* sweeping CRT scan beam (a long horizontal bar of light) */
        .fw-beam{position:absolute;left:0;right:0;height:150px;top:-150px;z-index:30;pointer-events:none;
          background:linear-gradient(to bottom,transparent,rgba(0,240,255,.05) 55%,rgba(120,240,255,.18));
          border-bottom:1px solid rgba(160,245,255,.5);box-shadow:0 0 26px rgba(0,240,255,.28);
          animation:fwbeam 4.8s linear infinite}
        @keyframes fwbeam{0%{transform:translateY(0)}100%{transform:translateY(calc(100vh + 150px))}}

        /* scanlines + chromatic vignette */
        .fw-scanlines{position:absolute;inset:0;pointer-events:none;z-index:31;
          background:repeating-linear-gradient(0deg,rgba(0,0,0,0) 0 1px,rgba(0,0,0,.28) 2px,rgba(0,0,0,0) 3px);
          opacity:.5;animation:fwflk 5s steps(50) infinite}
        @keyframes fwflk{0%,100%{opacity:.42}50%{opacity:.55}}
        .fw-vignette{position:absolute;inset:0;pointer-events:none;z-index:32;
          background:radial-gradient(ellipse at center,rgba(0,240,255,.05) 0%,rgba(8,3,20,0) 42%,rgba(4,1,12,.78) 100%)}

        /* HUD frame with neon corner brackets */
        .fw-frame{position:absolute;inset:7px;z-index:33;pointer-events:none;border-radius:6px;
          border:1px solid rgba(0,240,255,.18);box-shadow:inset 0 0 36px rgba(157,75,255,.16)}
        .fw-frame i{position:absolute;width:18px;height:18px;border:2px solid #00f0ff;filter:drop-shadow(0 0 6px #00f0ff)}
        .fw-frame i.tl{left:-1px;top:-1px;border-right:0;border-bottom:0}
        .fw-frame i.tr{right:-1px;top:-1px;border-left:0;border-bottom:0;border-color:#ff2bd6;filter:drop-shadow(0 0 6px #ff2bd6)}
        .fw-frame i.bl{left:-1px;bottom:-1px;border-right:0;border-top:0;border-color:#ff2bd6;filter:drop-shadow(0 0 6px #ff2bd6)}
        .fw-frame i.br{right:-1px;bottom:-1px;border-left:0;border-top:0}

        /* neon markers */
        .fw-divicon{background:transparent;border:none}
        .fw-marker{position:relative;display:block;width:20px;height:20px}
        .fw-dot{position:absolute;left:50%;top:50%;width:10px;height:10px;border-radius:999px;transform:translate(-50%,-50%);
          background:var(--c);box-shadow:0 0 8px var(--c),0 0 18px var(--c),0 0 3px #fff}
        .fw-pulse{position:absolute;left:50%;top:50%;width:10px;height:10px;border-radius:999px;transform:translate(-50%,-50%);
          background:var(--c);opacity:.6;animation:fwpulse 2.4s ease-out infinite}
        @keyframes fwpulse{0%{transform:translate(-50%,-50%) scale(1);opacity:.6}70%{opacity:.08}100%{transform:translate(-50%,-50%) scale(4.5);opacity:0}}
        /* vertical light beam shooting up from live nodes */
        .fw-beam-up{position:absolute;left:50%;bottom:50%;width:2px;height:42px;transform:translateX(-50%);
          background:linear-gradient(to top,var(--c),transparent);box-shadow:0 0 8px var(--c);opacity:.8;animation:fwflk 1.6s steps(20) infinite}

        /* popup */
        .fw-crt-root .leaflet-popup-content-wrapper{background:#0d0726;color:#eae0cd;border:1px solid #ff2bd6;border-radius:10px;box-shadow:0 0 18px #ff2bd655,inset 0 0 14px #00f0ff22}
        .fw-crt-root .leaflet-popup-tip{background:#0d0726;border:1px solid #ff2bd6}
        .fw-crt-root .leaflet-popup-content{margin:.6rem .8rem}
        .fw-crt-root a.leaflet-popup-close-button{color:#9d7bd6}
        .fw-pop-name{font-size:.98rem;color:#eae0cd;text-shadow:0 0 8px #00f0ff66}
        .fw-pop-time{font-size:.72rem;color:#9d7bd6;margin-top:1px}
        .fw-pop-badge{display:inline-block;margin-top:.45rem;font-size:.6rem;letter-spacing:.1em;text-transform:uppercase;padding:.18rem .5rem;border-radius:999px;border:1px solid currentColor;text-shadow:0 0 6px currentColor}

        .fw-overlay{position:absolute;inset:0;z-index:35;display:flex;align-items:center;justify-content:center;pointer-events:none;color:#9d7bd6;letter-spacing:.3em;text-transform:uppercase;font-size:.8rem;text-shadow:0 0 10px #ff2bd6aa}
        .fw-legend{position:absolute;left:12px;bottom:12px;z-index:34;display:flex;flex-direction:column;gap:4px;
          background:rgba(12,8,32,.6);border:1px solid #2a1750;border-radius:8px;padding:.45rem .6rem;font-size:.58rem;letter-spacing:.08em}
        .fw-legend span{display:inline-flex;align-items:center;gap:.45rem;color:#c9b8ee}
        .fw-legend i{width:8px;height:8px;border-radius:999px;display:inline-block;box-shadow:0 0 8px currentColor}
      `}</style>

      <div className="fw-crt-bar">
        <div>
          <div className="fw-crt-title">FLUSH//MAP</div>
          <div className="fw-crt-sub">{points.length} deployments · {ongoingCount} live · 7d</div>
        </div>
        <div style={{ display: "flex", gap: ".4rem", alignItems: "center" }}>
          <button
            className={`fw-btn ${optIn ? "fw-btn-on" : ""}`}
            onClick={toggleOptIn}
            title="Share precise GPS for your future activities. Takes effect on next start."
          >
            {optIn ? "◉ precise" : "≈ precise"}
          </button>
          <PanelMapToggle navigate={navigate} />
        </div>
      </div>

      <div className="fw-map-wrap">
        <MapContainer
          center={center}
          zoom={5}
          className="fw-map"
          zoomControl={false}
          worldCopyJump
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap &copy; CARTO'
          />
          {trail.length > 1 ? (
            <>
              <Polyline positions={trail} pathOptions={{ color: "#00f0ff", weight: 8, opacity: 0.1 }} />
              <Polyline positions={trail} pathOptions={{ color: "#7df9ff", weight: 1.4, opacity: 0.85, dashArray: "1 7" }} />
            </>
          ) : null}
          {points.map((p, i) => (
            <Marker key={i} position={[p.lat, p.lon]} icon={makeIcon(p)}>
              <Popup>
                <div className="fw-pop-name">{p.username}</div>
                <div className="fw-pop-time">
                  {p.ongoing ? "● ongoing now" : fmtTime(p.endedAt || p.startedAt)}
                </div>
                <div className="fw-pop-badge" style={{ color: metaFor(p.source).color }}>
                  {metaFor(p.source).icon} {metaFor(p.source).label}
                </div>
              </Popup>
            </Marker>
          ))}
          <FitBounds points={points} />
        </MapContainer>

        <div className="fw-floor" />
        <div className="fw-horizon" />
        <div className="fw-beam" />
        <div className="fw-scanlines" />
        <div className="fw-vignette" />
        <div className="fw-frame"><i className="tl" /><i className="tr" /><i className="bl" /><i className="br" /></div>

        <div className="fw-legend">
          <span><i style={{ background: "#39ff14", color: "#39ff14" }} /> opted in (precise)</span>
          <span><i style={{ background: "#ff9e2c", color: "#ff9e2c" }} /> approx · ip</span>
          <span><i style={{ background: "#ff2bd6", color: "#ff2bd6" }} /> approx · region</span>
        </div>

        {loading ? <div className="fw-overlay">scanning…</div> : null}
        {!loading && err ? <div className="fw-overlay">signal lost ({err})</div> : null}
        {!loading && !err && points.length === 0 ? (
          <div className="fw-overlay">no deployments in range</div>
        ) : null}
      </div>
    </div>
  );
}
